import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buscarProductos,
  crearProducto,
  TipoProducto,
  UnidadPeriodo,
  type ProductoDto,
} from "../api";
import Campo from "./Campo";

type Props = {
  tipo: TipoProducto;
  especieID?: number;
  productoID: number | null;
  productoNombre: string | null;
  nombreLibre: string | null;
  onSeleccionarProducto: (prod: ProductoDto | null) => void;
  onCambiarNombreLibre: (texto: string) => void;
  errores?: string[];
  requerido?: boolean;
};

const NOMBRES_TIPO_PRODUCTO: Record<number, string> = {
  [TipoProducto.Vacuna]: "Vacuna",
  [TipoProducto.AntiparasitarioInterno]: "Antiparasitario interno",
  [TipoProducto.AntiparasitarioExterno]: "Antiparasitario externo",
  [TipoProducto.Medicamento]: "Medicamento",
};

export default function BuscadorProducto({
  tipo,
  especieID,
  productoID,
  productoNombre,
  nombreLibre,
  onSeleccionarProducto,
  onCambiarNombreLibre,
  errores,
  requerido,
}: Props) {
  const qc = useQueryClient();
  const [busqueda, setBusqueda] = useState(nombreLibre ?? "");
  const [abierto, setAbierto] = useState(false);
  const [mostrarCrear, setMostrarCrear] = useState(false);

  // Formulario rápido para crear producto
  const [nuevoNombre, setNuevoNombre] = useState("");
  const [nuevoLab, setNuevoLab] = useState("");
  const [nuevaCantidad, setNuevaCantidad] = useState<number | "">("");
  const [nuevaUnidad, setNuevaUnidad] = useState<UnidadPeriodo | "">("");
  const [errorCreacion, setErrorCreacion] = useState<string | null>(null);

  const contenedorRef = useRef<HTMLDivElement>(null);

  const queryProductos = useQuery({
    queryKey: ["productos-buscar", { q: busqueda, tipo, especieID }],
    queryFn: () => buscarProductos({ q: busqueda, tipo, especieID }),
    enabled: abierto && busqueda.trim().length >= 0,
  });

  const mutacionCrear = useMutation({
    mutationFn: crearProducto,
    onSuccess: (prodCreado) => {
      qc.invalidateQueries({ queryKey: ["productos-buscar"] });
      onSeleccionarProducto(prodCreado);
      setBusqueda("");
      setAbierto(false);
      setMostrarCrear(false);
      setErrorCreacion(null);
    },
    onError: (err: Error) => {
      setErrorCreacion(err.message);
    },
  });

  useEffect(() => {
    function clickFuera(e: MouseEvent) {
      if (contenedorRef.current && !contenedorRef.current.contains(e.target as Node)) {
        setAbierto(false);
      }
    }
    document.addEventListener("mousedown", clickFuera);
    return () => document.removeEventListener("mousedown", clickFuera);
  }, []);

  const handleSeleccionar = (prod: ProductoDto) => {
    onSeleccionarProducto(prod);
    setBusqueda("");
    setAbierto(false);
  };

  const handleUsarTextoLibre = () => {
    onSeleccionarProducto(null);
    onCambiarNombreLibre(busqueda);
    setAbierto(false);
  };

  const handleLimpiar = () => {
    onSeleccionarProducto(null);
    onCambiarNombreLibre("");
    setBusqueda("");
  };

  const handleAbrirCrear = () => {
    setNuevoNombre(busqueda);
    setNuevoLab("");
    setNuevaCantidad("");
    setNuevaUnidad("");
    setErrorCreacion(null);
    setMostrarCrear(true);
    setAbierto(false);
  };

  const handleGuardarNuevo = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nuevoNombre.trim()) {
      setErrorCreacion("El nombre es obligatorio.");
      return;
    }
    mutacionCrear.mutate({
      tipo,
      nombre: nuevoNombre.trim(),
      laboratorio: nuevoLab.trim() || null,
      especieID: especieID ?? null,
      periodicidadCantidad: typeof nuevaCantidad === "number" ? nuevaCantidad : null,
      periodicidadUnidad: typeof nuevaUnidad === "number" ? nuevaUnidad : null,
    });
  };

  // Si ya hay un producto seleccionado del catálogo
  if (productoID) {
    return (
      <Campo
        etiqueta={NOMBRES_TIPO_PRODUCTO[tipo] ?? "Producto"}
        errores={errores}
        requerido={requerido}
      >
        <div className="producto-seleccionado">
          <span className="producto-tag">
            <strong>{productoNombre}</strong>
            <span className="etiqueta neutra">Catálogo</span>
          </span>
          <button
            type="button"
            className="boton-chico"
            onClick={handleLimpiar}
            title="Cambiar producto"
          >
            Cambiar
          </button>
        </div>
      </Campo>
    );
  }

  // Si se está usando texto libre
  if (nombreLibre) {
    return (
      <Campo
        etiqueta={`${NOMBRES_TIPO_PRODUCTO[tipo] ?? "Producto"} (personalizado)`}
        errores={errores}
        requerido={requerido}
      >
        <div className="producto-seleccionado">
          <span className="producto-tag">
            <span>{nombreLibre}</span>
            <span className="etiqueta neutra">Texto libre</span>
          </span>
          <button
            type="button"
            className="boton-chico"
            onClick={handleLimpiar}
            title="Cambiar a búsqueda de catálogo"
          >
            Cambiar
          </button>
        </div>
      </Campo>
    );
  }

  return (
    <div className="buscador-producto-contenedor" ref={contenedorRef}>
      <Campo
        etiqueta={`Buscar ${NOMBRES_TIPO_PRODUCTO[tipo]?.toLowerCase() ?? "producto"}`}
        errores={errores}
        requerido={requerido}
        ayuda="Escribe para buscar en el catálogo o ingresar un nombre personalizado"
      >
        <div className="buscador-input-wrap">
          <input
            type="text"
            placeholder={`Ej: ${tipo === TipoProducto.Vacuna ? "Óctuple, Antirrábica…" : "Nombre del producto…"}`}
            value={busqueda}
            onChange={(e) => {
              setBusqueda(e.target.value);
              setAbierto(true);
            }}
            onFocus={() => setAbierto(true)}
          />
        </div>
      </Campo>

      {abierto && (
        <div className="sugerencias-dropdown">
          {queryProductos.isPending && <p className="sugerencia-item muted">Buscando…</p>}

          {queryProductos.data && queryProductos.data.length > 0 && (
            <div className="sugerencias-lista">
              {queryProductos.data.map((prod) => (
                <button
                  type="button"
                  key={prod.productoID}
                  className="sugerencia-item"
                  onClick={() => handleSeleccionar(prod)}
                >
                  <div className="sugerencia-principal">
                    <span className="sugerencia-nombre">{prod.nombre}</span>
                    {prod.laboratorio && (
                      <span className="sugerencia-lab"> · {prod.laboratorio}</span>
                    )}
                  </div>
                  {prod.periodicidadCantidad && prod.periodicidadUnidad && (
                    <span className="sugerencia-periodo muted">
                      Cada {prod.periodicidadCantidad}{" "}
                      {prod.periodicidadUnidad === UnidadPeriodo.Dias
                        ? "días"
                        : prod.periodicidadUnidad === UnidadPeriodo.Meses
                          ? "meses"
                          : "años"}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {queryProductos.data && queryProductos.data.length === 0 && busqueda.trim() && (
            <p className="sugerencia-item muted">No se encontraron productos coincidentes.</p>
          )}

          {busqueda.trim().length > 0 && (
            <div className="sugerencias-acciones">
              <button
                type="button"
                className="sugerencia-accion"
                onClick={handleUsarTextoLibre}
              >
                ✏️ Usar <strong>&ldquo;{busqueda}&rdquo;</strong> como texto libre
              </button>
              <button
                type="button"
                className="sugerencia-accion"
                onClick={handleAbrirCrear}
              >
                ➕ Agregar <strong>&ldquo;{busqueda}&rdquo;</strong> a mi catálogo
              </button>
            </div>
          )}
        </div>
      )}

      {mostrarCrear && (
        <div className="modal-crear-producto panel">
          <h3>Guardar nuevo {NOMBRES_TIPO_PRODUCTO[tipo]?.toLowerCase()} en catálogo</h3>
          <p className="muted" style={{ fontSize: "0.85rem", margin: "0 0 0.75rem" }}>
            Al guardarlo en el catálogo se podrá reutilizar en futuras visitas y programará recordatorios automáticos si defines su periodicidad.
          </p>

          <form onSubmit={handleGuardarNuevo} className="formulario">
            <Campo etiqueta="Nombre del producto" requerido>
              <input
                type="text"
                value={nuevoNombre}
                onChange={(e) => setNuevoNombre(e.target.value)}
                required
              />
            </Campo>

            <div className="fila">
              <Campo etiqueta="Laboratorio (opcional)">
                <input
                  type="text"
                  value={nuevoLab}
                  onChange={(e) => setNuevoLab(e.target.value)}
                  placeholder="Ej: Zoetis, Boehringer…"
                />
              </Campo>

              <Campo etiqueta="Repetir cada (opcional)" ayuda="Para recordatorios automáticos">
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <input
                    type="number"
                    min="1"
                    value={nuevaCantidad}
                    onChange={(e) =>
                      setNuevaCantidad(e.target.value ? Number(e.target.value) : "")
                    }
                    placeholder="Ej: 1, 3, 12"
                    style={{ width: "5rem" }}
                  />
                  <select
                    value={nuevaUnidad}
                    onChange={(e) =>
                      setNuevaUnidad(e.target.value ? (Number(e.target.value) as UnidadPeriodo) : "")
                    }
                  >
                    <option value="">— Unidad —</option>
                    <option value={UnidadPeriodo.Dias}>Días</option>
                    <option value={UnidadPeriodo.Meses}>Meses</option>
                    <option value={UnidadPeriodo.Anios}>Años</option>
                  </select>
                </div>
              </Campo>
            </div>

            {errorCreacion && <p className="err">{errorCreacion}</p>}

            <div className="acciones">
              <button
                type="submit"
                className="boton-primario"
                disabled={mutacionCrear.isPending}
              >
                {mutacionCrear.isPending ? "Guardando…" : "Guardar en catálogo"}
              </button>
              <button type="button" onClick={() => setMostrarCrear(false)}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
